import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { fetchEmployeeDesignations } from 'actions';
import { map, find, isArray, isEmpty, isEqual } from 'lodash';
import { CustomDropdownContainer, CustomMultiOption } from 'CommonComponent';

const optionMatchesId = (opt, id) =>
  opt != null && String(opt.value) === String(id);

const dedupeOptionsByValue = (options = []) => {
  const seen = new Set();
  return options.filter((opt) => {
    if (!opt || opt.value == null) return false;
    const key = String(opt.value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const DesignationDropdown = ({
  selectedId = '',
  selectedChangingId = '',
  designationValue,
  setDesignationValue,
  designationOnChange,
  setAllDesignationIds,
  showSelectAllOption = true,
  isMulti = true,
  placeholder = 'Select Designation',
  label = 'Designation',
  name = 'designation_id',
  isDisabled = false,
  required = false,
}) => {
  const dispatch = useDispatch();
  const designationList = useSelector(state => state.employeeState.designationsList);
  const isDesignationsLoading = useSelector(state => state.employeeState.isDesignationsLoading);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if ((!designationList || designationList.length === 0) && !isDesignationsLoading) {
      dispatch(fetchEmployeeDesignations());
    }
  }, [dispatch, designationList, isDesignationsLoading]);

  useEffect(() => {
    if (!designationList) return;

    let formattedOptions = map(designationList, (item) => ({
      value: item.id,
      label: item.name || item.designation_name,
    }));

    if (showSelectAllOption && isMulti) {
      formattedOptions.unshift({ value: 'all', label: 'All' });
    }

    if (isMulti && Array.isArray(designationValue)) {
      designationValue.forEach((opt) => {
        if (opt && opt.value && opt.value !== 'all' && !find(formattedOptions, (o) => optionMatchesId(o, opt.value))) {
          formattedOptions.push(opt);
        }
      });
    }

    setOptions(dedupeOptionsByValue(formattedOptions));

    if (typeof setAllDesignationIds === 'function') {
      setAllDesignationIds(map(designationList, 'id'));
    }
    // designationValue omitted — including it looped parent form updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designationList, showSelectAllOption, isMulti]);

  // Hydrate from selectedId only when IDs/list change — not on every value edit.
  useEffect(() => {
    if (isEmpty(selectedId)) return;
    if (!designationList || designationList.length === 0) return;

    const formattedOptions = map(designationList, (item) => ({
      value: item.id,
      label: item.name || item.designation_name,
    }));

    if (isArray(selectedId)) {
      const uniqueIds = [...new Set(selectedId.map((id) => String(id)))];
      const currentMulti = isArray(designationValue) ? designationValue : [];
      const selected = dedupeOptionsByValue(
        uniqueIds
          .map(
            (id) =>
              find(formattedOptions, (o) => optionMatchesId(o, id)) ||
              find(currentMulti, (o) => optionMatchesId(o, id))
          )
          .filter(Boolean)
      );
      if (!isEqual(designationValue, selected)) {
        setDesignationValue(selected);
      }
    } else {
      const currentSingle =
        designationValue && !isArray(designationValue) ? designationValue : null;
      const selected =
        find(formattedOptions, (o) => optionMatchesId(o, selectedId)) ||
        (currentSingle && optionMatchesId(currentSingle, selectedId) ? currentSingle : null);
      if (!isEqual(designationValue, selected)) {
        setDesignationValue(selected || null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designationList, selectedId]);

  useEffect(() => {
    if (!selectedChangingId || !designationList) return;

    if (isArray(selectedChangingId)) {
      const selected = dedupeOptionsByValue(
        selectedChangingId
          .map((id) => find(designationList, (item) => item && String(item.id) === String(id)))
          .filter(Boolean)
          .map((item) => ({
            value: item.id,
            label: item.name || item.designation_name,
          }))
      );
      setDesignationValue(selected);
    } else {
      const selected = find(
        designationList,
        (item) => item && String(item.id) === String(selectedChangingId)
      );
      setDesignationValue(
        selected ? { value: selected.id, label: selected.name || selected.designation_name } : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChangingId, designationList]);

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label className={`label-input${required ? " label-input--required" : ""}`}>{label}</Form.Label>
        <Select
          name={name}
          options={options}
          value={designationValue}
          onChange={designationOnChange}
          isMulti={isMulti}
          placeholder={placeholder}
          className="multi-select w-100"
          classNamePrefix="react-select"
          isSearchable
          isClearable
          isDisabled={isDisabled}
          hideSelectedOptions={false}
          closeMenuOnSelect={!isMulti}
          openMenuOnFocus
          components={{
            ValueContainer: CustomDropdownContainer,
            ...(isMulti ? { Option: CustomMultiOption } : {}),
          }}
        />
      </Form.Group>
    </>
  );
};

DesignationDropdown.propTypes = {
  designationOnChange: PropTypes.func.isRequired,
  setDesignationValue: PropTypes.func.isRequired,
  designationValue: PropTypes.any,
  selectedId: PropTypes.any,
  selectedChangingId: PropTypes.any,
  setAllDesignationIds: PropTypes.func,
  isMulti: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  isDisabled: PropTypes.bool,
  required: PropTypes.bool,
};

export default DesignationDropdown;
